<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from "vue";

const props = defineProps<{
  src: string;
  name?: string;
  artist?: string;
}>();

const open = defineModel<boolean>("open", { default: false });

const isZoomed = ref(false);
const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);

const containerRef = useTemplateRef<HTMLElement>("container");
const imgRef = useTemplateRef<HTMLImageElement>("image");

// Mobile touch tracking states
let touchStartX = 0;
let touchStartY = 0;
let initialTranslateX = 0;
let initialTranslateY = 0;
let isDragging = false;

const closeModal = () => {
  open.value = false;
};

const clampTranslations = () => {
  if (!imgRef.value || !containerRef.value) return;
  const rect = imgRef.value.getBoundingClientRect();
  const containerRect = containerRef.value.getBoundingClientRect();
  
  // Calculate maximum allowed translation based on current scaled dimensions
  const maxX = Math.max(0, (rect.width - containerRect.width) / 2);
  const maxY = Math.max(0, (rect.height - containerRect.height) / 2);
  
  translateX.value = Math.max(-maxX, Math.min(maxX, translateX.value));
  translateY.value = Math.max(-maxY, Math.min(maxY, translateY.value));
};

const toggleZoom = (e: MouseEvent | TouchEvent) => {
  if (scale.value > 1) {
    resetZoom();
  } else {
    isZoomed.value = true;
    scale.value = 2.5;
    updatePanFromPointer(e);
  }
};

// Proportional panning for desktop hover/mousemove
const updatePanFromPointer = (e: MouseEvent | TouchEvent) => {
  if (!containerRef.value || !isZoomed.value) return;
  
  // If it's a mobile touch event, we handle dragging separately
  if (window.TouchEvent && e instanceof TouchEvent) {
    return;
  }
  
  const rect = containerRef.value.getBoundingClientRect();
  const mouseEvent = e as MouseEvent;
  
  // Calculate cursor position relative to container (from 0 to 1)
  const pctX = (mouseEvent.clientX - rect.left) / rect.width;
  const pctY = (mouseEvent.clientY - rect.top) / rect.height;
  
  // Map center of screen (0.5) to translate(0, 0)
  // Map edges to translate limits
  const maxTranslationX = (rect.width * (scale.value - 1)) / 2;
  const maxTranslationY = (rect.height * (scale.value - 1)) / 2;
  
  translateX.value = (0.5 - pctX) * maxTranslationX * 2;
  translateY.value = (0.5 - pctY) * maxTranslationY * 2;
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isZoomed.value || isDragging) return;
  updatePanFromPointer(e);
};

// Incremental Zoom Buttons
const zoomIn = () => {
  isZoomed.value = true;
  scale.value = Math.min(4, scale.value + 0.25);
  nextTick(clampTranslations);
};

const zoomOut = () => {
  scale.value = Math.max(1, scale.value - 0.25);
  if (scale.value === 1) {
    isZoomed.value = false;
    translateX.value = 0;
    translateY.value = 0;
  } else {
    nextTick(clampTranslations);
  }
};

const resetZoom = () => {
  isZoomed.value = false;
  scale.value = 1;
  translateX.value = 0;
  translateY.value = 0;
};

// Mobile Touch Dragging Logic
const handleTouchStart = (e: TouchEvent) => {
  if (!isZoomed.value) return;
  if (e.touches.length === 1) {
    isDragging = true;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    initialTranslateX = translateX.value;
    initialTranslateY = translateY.value;
  }
};

const handleTouchMove = (e: TouchEvent) => {
  if (!isZoomed.value || !isDragging || !imgRef.value || !containerRef.value) return;
  
  if (e.cancelable) {
    e.preventDefault();
  }
  
  if (e.touches.length === 1) {
    const dx = e.touches[0].clientX - touchStartX;
    const dy = e.touches[0].clientY - touchStartY;
    
    translateX.value = initialTranslateX + dx;
    translateY.value = initialTranslateY + dy;
    
    clampTranslations();
  }
};

const handleTouchEnd = () => {
  isDragging = false;
};

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && open.value) {
    closeModal();
  }
};

watch(open, (newVal) => {
  if (newVal) {
    resetZoom();
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
});

onMounted(() => {
  window.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
  document.body.style.overflow = "";
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center bg-white/98 backdrop-blur-xl"
        @click="closeModal"
      >
        <!-- Header Overlay -->
        <div class="absolute top-0 left-0 right-0 p-4 md:p-6 flex justify-between items-center z-10 pointer-events-none">
          <div class="text-on-primary-heading select-none truncate pr-4 pointer-events-auto">
            <span v-if="name" class="text-base md:text-lg font-semibold tracking-wide">{{ name }}</span>
            <span v-if="name && artist" class="mx-2 text-on-primary-body/40">•</span>
            <span v-if="artist" class="text-on-primary-body text-sm font-light">by {{ artist }}</span>
          </div>
          <button
            type="button"
            @click.stop="closeModal"
            class="flex items-center justify-center size-10 bg-white border border-on-primary-border text-on-primary-heading rounded-full shadow-sm hover:bg-button-secondary transition active:scale-95 cursor-pointer shrink-0 pointer-events-auto"
            aria-label="Close zoom modal"
          >
            <Icon name="lucide:x" class="size-5" />
          </button>
        </div>

        <!-- Fully Immersive Screen-Sized Viewport -->
        <div
          class="w-full h-full flex items-center justify-center overflow-hidden cursor-zoom-in"
          :class="{ 'cursor-zoom-out': scale > 1 }"
          ref="container"
          @click.stop="toggleZoom"
          @mousemove="handleMouseMove"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <img
            ref="image"
            :src="src"
            :style="{
              transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
            }"
            :class="[
              'max-h-[85vh] max-w-[85vw] object-contain select-none transition-transform ease-out border border-on-primary-border/40 shadow-sm bg-white',
              isZoomed ? 'duration-150' : 'duration-300'
            ]"
            draggable="false"
            alt=""
          />
        </div>

        <!-- Floating Zoom Controls (Matching the IN/CM toggle styling palette) -->
        <div class="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-20 bg-button-secondary border border-on-primary-border p-1 rounded-full shadow-sm">
          <button
            type="button"
            @click.stop="zoomIn"
            class="flex items-center justify-center size-10 bg-white text-on-primary-heading rounded-full shadow-sm hover:bg-button-secondary transition active:scale-90 cursor-pointer"
            title="Zoom In"
            aria-label="Zoom In"
            :disabled="scale >= 4"
            :class="{ 'opacity-40 cursor-not-allowed': scale >= 4 }"
          >
            <Icon name="lucide:plus" class="size-5" />
          </button>
          
          <button
            type="button"
            @click.stop="zoomOut"
            class="flex items-center justify-center size-10 bg-white text-on-primary-heading rounded-full shadow-sm hover:bg-button-secondary transition active:scale-90 cursor-pointer"
            title="Zoom Out"
            aria-label="Zoom Out"
            :disabled="scale <= 1"
            :class="{ 'opacity-40 cursor-not-allowed': scale <= 1 }"
          >
            <Icon name="lucide:minus" class="size-5" />
          </button>
          
          <button
            type="button"
            @click.stop="resetZoom"
            class="flex items-center justify-center size-10 bg-white text-on-primary-heading rounded-full shadow-sm hover:bg-button-secondary transition active:scale-90 cursor-pointer"
            title="Reset Zoom"
            aria-label="Reset Zoom"
            :disabled="scale === 1"
            :class="{ 'opacity-40 cursor-not-allowed': scale === 1 }"
          >
            <Icon name="lucide:maximize-2" class="size-5" />
          </button>
        </div>

        <!-- Footer Overlay Hint -->
        <div class="absolute bottom-0 left-0 right-0 p-6 flex justify-center items-center z-10 pointer-events-none select-none">
          <span class="text-[11px] md:text-xs text-on-primary-body bg-button-secondary border border-on-primary-border px-4 py-1.5 rounded-full tracking-wider shadow-sm pointer-events-auto">
            {{ scale > 1 ? `Zoom: ${Math.round(scale * 100)}% • Move cursor or drag to pan` : 'Click artwork to inspect details' }}
          </span>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
